<script setup lang="ts">
import { ApolloError } from '@apollo/client';
import { useMutation } from '@vue/apollo-composable';
import { useWindowSize } from '@vueuse/core';
import { computed, ref, useTemplateRef, watch } from 'vue';

import { LanguageType } from '@/__generated__/gateway/graphql';
import TextArea from '@/components/Inputs/TextArea.vue';
import TextInput from '@/components/Inputs/TextInput.vue';
import AddStars from '@/components/ProductReviews/AddStars.vue';
import AdaptiveModal from '@/shared/components/UI/AdaptiveModal.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import IconChevron from '@/shared/components/UI/Icons/IconChevron.vue';
import { CreateReviewMutation } from '@/shared/composables/productReviews/productReviewsGateway.mutations';
import { useReviewFormValidation } from '@/shared/composables/productReviews/useReviewFormValidation';
import { useTranslations } from '@/shared/composables/useTranslations';
import { breakpoints, posthogEvents } from '@/shared/constants/constants';
import { useProductStore } from '@/shared/pinia/modules/product';
import { getLanguageType } from '@/shared/utils/gateway/helpers';
import { shopifyIdToGid } from '@/shared/utils/shopify/id-formatter';
import { trackHobbiiEvent } from '@/shared/utils/tracking';

const { t } = useTranslations();
const { width } = useWindowSize();
const productStore = useProductStore();

const { productId, active, initialRating } = defineProps<{
  productId: string;
  active: boolean;
  initialRating: number;
}>();

const rating = ref(initialRating);
const reviewDescriptionRef = useTemplateRef('review-description');
const reviewTitleRef = useTemplateRef('review-title');

watch(
  () => initialRating,
  (value) => {
    rating.value = value;
  }
);

const emit = defineEmits<{
  'update:active': [value: boolean];
  'review-submitted': [error: ApolloError | null];
}>();

const propModel = computed({
  get: () => active,
  set: (value) => {
    emit('update:active', value);
  }
});

const {
  descriptionMinLength,
  descriptionMaxLength,
  hasErrorState,
  validate,
  resetFormDirtyState,
  titleMaxLength,
  ratingDescription,
  ratingTitle
} = useReviewFormValidation();

const descriptionTooLong = computed(
  () => ratingDescription.value.length > descriptionMaxLength
);

const {
  loading: mutating,
  mutate,
  error
} = useMutation(CreateReviewMutation, {
  clientId: 'gateway'
});

const data = computed(() => {
  return {
    input: {
      product_id: productId,
      rating: rating.value,
      title: ratingTitle.value,
      body_text: ratingDescription.value,
      language: getLanguageType(window.Shopify.locale, LanguageType.En)
    }
  };
});
const submitReview = async () => {
  await mutate(data.value);

  emit('review-submitted', error.value);
  if (error.value || !productStore.productId || !productStore.productName) {
    return;
  }

  window.posthog?.capture(posthogEvents.REVIEW_ADDED, {
    product_id: productStore.productId,
    product_name: productStore.productName,
    title_length: ratingTitle.value.length,
    body_length: ratingDescription.value.length,
    rating: rating.value
  });

  trackHobbiiEvent('add_review', {
    category: 'Product Review',
    item_id: shopifyIdToGid(productStore.productId, 'Product'),
    item_name: productStore.productName,
    label: productStore.productName,
    value: rating.value
  });
};

const handleSubmit = () => {
  resetFormDirtyState();

  if (!validate()) {
    return;
  }

  submitReview();
};
</script>
<template>
  <AdaptiveModal
    v-model:active="propModel"
    :modal-props="{ size: 'lg', closeLabel: t('global.close') }"
    :drawer-props="{ closeLabel: t('global.close') }"
  >
    <template v-if="width < breakpoints.md" #header>
      <h3 class="mx-auto text-center text-base font-bold text-black">
        {{ t('reviews.write_a_review') }}
      </h3>
    </template>
    <form
      class="relative size-full overflow-y-auto px-4 pb-6 md:p-10"
      @submit.prevent="handleSubmit"
    >
      <h3
        v-if="width > breakpoints.md"
        class="mx-auto mb-4 text-center text-xl font-bold leading-none text-black"
      >
        {{ t('reviews.write_a_review') }}
      </h3>
      <p class="text-center text-sm text-black">
        {{ t('reviews.share_your_thoughts') }}
      </p>
      <div class="mt-8 flex w-full flex-col items-center gap-4 text-center">
        <div class="text-sm font-bold">
          {{ t('reviews.your_overall_rating') }}
        </div>
        <div class="flex w-full flex-col items-center justify-center">
          <AddStars
            :initial-rating="initialRating"
            :product-id="productId"
            class="hb-centered"
            @click="rating = $event"
          />
        </div>
      </div>
      <div class="mt-10 flex flex-col gap-6 text-black">
        <div class="flex flex-col gap-2">
          <label
            class="text-sm font-bold"
            :for="reviewDescriptionRef?.textareaElement?.id"
            >{{ t('reviews.your_review') }}
            <span class="font-normal text-error">*</span></label
          >
          <TextArea
            ref="review-description"
            v-model.trim="ratingDescription"
            :placeholder="t('reviews.tell_us_more_about_your_experience')"
            :error="hasErrorState('description') || descriptionTooLong"
          />
          <div class="-mt-1 text-xs">
            <div v-if="descriptionTooLong">
              {{
                t('reviews.rating_length_limit_exceeded', {
                  n: ratingDescription.length - descriptionMaxLength
                })
              }}
            </div>
            <span
              v-else
              :class="{
                'text-error': hasErrorState('description') || descriptionTooLong
              }"
            >
              {{
                t('reviews.review_min_XX_chars', {
                  min: descriptionMinLength
                })
              }}</span
            >
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label
            class="text-sm font-bold"
            :for="reviewTitleRef?.inputElement?.id"
            >{{ t('reviews.title') }}
            <span class="font-normal text-error">*</span></label
          >
          <TextInput
            ref="review-title"
            v-model.trim="ratingTitle"
            :placeholder="t('reviews.give_review_a_title')"
            :error="hasErrorState('title')"
          />
          <div class="-mt-1 text-xs">
            <span
              :class="{
                'text-error':
                  hasErrorState('title') || ratingTitle.length > titleMaxLength
              }"
              >{{
                t('reviews.title_max_XX_chars', {
                  max: 50,
                  count: ratingTitle.length
                })
              }}</span
            >
          </div>
        </div>
        <div class="flex flex-col gap-3">
          <ButtonText
            full-width
            size="lg"
            :loading="mutating"
            :disabled="mutating"
            @click="handleSubmit"
          >
            {{ t('reviews.submit_review') }}
            <template #icon
              ><IconChevron direction="right" class="text-xl"
            /></template>
          </ButtonText>
        </div>
      </div>
    </form>
  </AdaptiveModal>
</template>
